// src/pages/ResultPage.tsx

import { useLocation, useNavigate } from 'react-router-dom';
import type { TriageResponsePayload, TriageLevel } from '../types/triage';
import { useEffect } from 'react';

export function ResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { triageResponse, measurements } = location.state || {};

  useEffect(() => {
    if (!triageResponse) {
      navigate('/');
    }
  }, [triageResponse, navigate]);

  if (!triageResponse) {
    return null;
  }

  const response = triageResponse as TriageResponsePayload;

  const getLevelColor = (level: TriageLevel): string => {
    switch (level) {
      case 'red':
        return 'bg-triage-red text-white';
      case 'yellow':
        return 'bg-triage-yellow text-black';
      case 'green':
        return 'bg-triage-green text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getLevelText = (level: TriageLevel): string => {
    switch (level) {
      case 'red':
        return 'IMMEDIATE';
      case 'yellow':
        return 'URGENT';
      case 'green':
        return 'NON-URGENT';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
          Triage Result
        </h1>

        {/* Triage Level Badge */}
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 mb-4 sm:mb-6">
          <div className="text-center">
            <div className="text-xs sm:text-sm font-medium text-gray-600 mb-2 sm:mb-3">
              TRIAGE LEVEL
            </div>
            <div
              className={`inline-block px-8 sm:px-12 py-4 sm:py-6 rounded-lg font-bold text-2xl sm:text-3xl ${getLevelColor(
                response.triage_level
              )}`}
            >
              {getLevelText(response.triage_level)}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Summary</h2>
          <p className="text-sm sm:text-base text-gray-700 whitespace-pre-line">{response.summary}</p>
        </div>

        {/* Recommended Actions */}
        {response.recommended_actions && response.recommended_actions.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
              Recommended Actions
            </h2>
            <ul className="space-y-2">
              {response.recommended_actions.map((action, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2 sm:mr-3 text-blue-600 font-bold flex-shrink-0">{index + 1}.</span>
                  <span className="text-sm sm:text-base text-gray-700">{action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Measurement Data Summary */}
        {measurements && (
          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
              Measurement Data
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="font-medium text-gray-600 block mb-1">Heart Rate:</span>
                <span className="text-gray-900 font-semibold">
                  {measurements.vitals.hr_bpm
                    ? `${Math.round(measurements.vitals.hr_bpm)} bpm`
                    : 'N/A'}
                </span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="font-medium text-gray-600 block mb-1">Respiratory Rate:</span>
                <span className="text-gray-900 font-semibold">
                  {measurements.vitals.rr_bpm
                    ? `${Math.round(measurements.vitals.rr_bpm)} /min`
                    : 'N/A'}
                </span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="font-medium text-gray-600 block mb-1">Posture:</span>
                <span className="text-gray-900 font-semibold capitalize">
                  {measurements.posture.label}
                </span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="font-medium text-gray-600 block mb-1">Respiratory Effort:</span>
                <span className="text-gray-900 font-semibold">
                  {measurements.respiratory_effort_score} / 3
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/')}
            className="w-full sm:flex-1 px-6 py-3 bg-blue-600 text-white text-base font-semibold rounded-lg hover:bg-blue-700 active:bg-blue-800 transition touch-manipulation"
          >
            New Patient
          </button>

          <button
            onClick={() => window.print()}
            className="w-full sm:flex-1 px-6 py-3 bg-gray-600 text-white text-base font-semibold rounded-lg hover:bg-gray-700 active:bg-gray-800 transition touch-manipulation"
          >
            Print Results
          </button>
        </div>

        {/* Debug Info (if available) */}
        {response.debug_info && (
          <details className="mt-4 sm:mt-6 bg-gray-100 rounded-lg p-3 sm:p-4">
            <summary className="cursor-pointer font-medium text-sm sm:text-base text-gray-700">
              Debug Information
            </summary>
            <pre className="mt-3 text-xs text-gray-600 overflow-auto">
              {JSON.stringify(response.debug_info, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
